/**
 * useCompanionFeed — web port. Adapts useMessages + WebData callbacks into
 * a unified CompanionCard-based feed. Same surface as RN.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessages } from './useMessages';
import type {
  CompanionCard,
  CompanionCardKind,
  CompanionSession,
} from '../types/companion';
import type { ChatHistoryItem, ChatMessageItem } from '../api/chatApi';

interface UseCompanionFeedOptions {
  sessionId: string | null | undefined;
  webDataItems?: Array<{
    id: string;
    src: { uri?: string; html?: string };
    updatedAt?: number;
  }>;
  enabled?: boolean;
}

interface UseCompanionFeedReturn {
  sessions: CompanionSession[];
  cards: CompanionCard[];
  sendText: (text: string) => Promise<void>;
  scanImage: (image: { data: string; mime: string }) => Promise<void>;
  isLoading: boolean;
  latestCards: CompanionCard[];
  handleWebDataItems: (
    items: Array<{ id: string; src: { uri?: string; html?: string } }>,
  ) => void;
}

function contentTypeToKind(type: string): CompanionCardKind | null {
  switch (type) {
    case 'text':
      return 'text';
    case 'card':
      return 'card';
    case 'map':
      return 'map';
    case 'image':
      return 'image';
    case 'actions':
      return 'action';
    case 'system':
      return 'system';
    case 'menu_recommendations':
      return 'menu_recommendations';
    case 'scene_info':
      return 'scene_info';
    case 'loading':
    case 'voice':
      return null;
    default:
      return null;
  }
}

function messageToCards(message: ChatHistoryItem): CompanionCard[] {
  if (message.role === 'user') return [];
  const cards: CompanionCard[] = [];
  const timestamp = new Date(message.created_at).getTime();
  for (let i = 0; i < message.content.length; i++) {
    const item: ChatMessageItem = message.content[i];
    const kind = contentTypeToKind(item.type);
    if (!kind) continue;
    const interactive = kind === 'card' || kind === 'action' || kind === 'web';
    cards.push({
      id: `${message.id}-${i}`,
      kind,
      content: item,
      sessionId: message.session_id,
      timestamp: timestamp + i,
      interactive,
      actions:
        kind === 'card' && (item as any).action
          ? [
              {
                label: (item as any).action.label || 'View',
                icon: 'open-outline',
                type: 'navigate',
                payload: (item as any).action,
              },
            ]
          : undefined,
      source: 'message',
    });
  }
  return cards;
}

export function useCompanionFeed(
  options: UseCompanionFeedOptions,
): UseCompanionFeedReturn {
  const { sessionId, webDataItems, enabled = true } = options;
  const messagesHook = useMessages(sessionId, { enabled });

  const [webDataCards, setWebDataCards] = useState<CompanionCard[]>([]);
  const seenWebDataIds = useRef(new Set<string>());

  const handleWebDataItems = useCallback(
    (items: Array<{ id: string; src: { uri?: string; html?: string } }>) => {
      if (!sessionId) return;
      const newCards: CompanionCard[] = [];
      for (const item of items) {
        if (seenWebDataIds.current.has(item.id)) continue;
        seenWebDataIds.current.add(item.id);
        newCards.push({
          id: `webdata-${item.id}`,
          kind: 'web',
          content: item.src,
          sessionId,
          timestamp: Date.now(),
          interactive: true,
          source: 'webdata',
        });
      }
      if (newCards.length > 0) {
        setWebDataCards(prev => [...prev, ...newCards]);
      }
    },
    [sessionId],
  );

  useEffect(() => {
    if (webDataItems && webDataItems.length > 0) {
      handleWebDataItems(webDataItems);
    }
  }, [webDataItems, handleWebDataItems]);

  useEffect(() => {
    setWebDataCards([]);
    seenWebDataIds.current.clear();
  }, [sessionId]);

  const messageCards = useMemo(() => {
    const result: CompanionCard[] = [];
    for (const m of messagesHook.messages) result.push(...messageToCards(m));
    return result;
  }, [messagesHook.messages]);

  const cards = useMemo(() => {
    const all = [...messageCards, ...webDataCards];
    all.sort((a, b) => a.timestamp - b.timestamp);
    return all;
  }, [messageCards, webDataCards]);

  const sessions = useMemo<CompanionSession[]>(() => {
    if (!sessionId || cards.length === 0) return [];
    return [
      {
        sessionId,
        startedAt:
          cards.length > 0
            ? new Date(cards[0].timestamp).toISOString()
            : new Date().toISOString(),
        cards,
      },
    ];
  }, [sessionId, cards]);

  const latestCards = useMemo(() => cards.slice(-2), [cards]);

  const sendText = useCallback(
    async (text: string) => {
      await messagesHook.sendText(text);
    },
    [messagesHook],
  );

  const scanImage = useCallback(
    async (image: { data: string; mime: string }) => {
      if (!sessionId) return;
      const { scanScene } = await import('../api/menuApi');
      await scanScene(sessionId, image);
      await messagesHook.refresh();
    },
    [sessionId, messagesHook],
  );

  return {
    sessions,
    cards,
    sendText,
    scanImage,
    isLoading: messagesHook.loading,
    latestCards,
    handleWebDataItems,
  };
}

export default useCompanionFeed;
